// Connecteur LOOKBACK — séances de tests et d'entretiens, importées dans une
// étude comme des entretiens transcrits.
//
// Pas de jeton à coller : un administrateur CONNECTE le compte Lookback (il
// part sur Lookback donner son accord, puis revient ici). Ensuite, quiconque
// peut écrire dans le projet parcourt projets → rounds → séances et importe
// les transcriptions. La vidéo reste dans Lookback ; l'entretien garde le lien.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  useDeleteConnector,
  useLookbackBrowse,
  useLookbackConnect,
  useLookbackImport,
  useLookbackTools,
  useSaveConnector,
  useStudies,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Connector, LookbackImportResult, LookbackItem } from '../api/types';
import { Button, ErrorBanner, Field, Note, Select, Spinner } from '../components/ui';
import { Drawer } from '../write/Drawer';
import { useCanWrite } from '../write/useCanWrite';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const fmtDay = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Envoie le navigateur sur l'écran d'autorisation de Lookback. */
function useStartConnect() {
  const connect = useLookbackConnect();
  const start = useCallback((connectorId: string) => {
    connect.mutate(connectorId, { onSuccess: ({ url }) => window.location.assign(url) });
  }, [connect]);
  return { start, pending: connect.isPending, error: connect.error };
}

// --- Retour de l'écran d'autorisation ---------------------------------------------

const RETURN_MESSAGES: Record<string, { ok: boolean; text: string }> = {
  connecte: { ok: true, text: 'Lookback est connecté. Vous pouvez importer des séances dans une étude.' },
  refuse: { ok: false, text: 'La connexion à Lookback a été annulée : l’accès n’a pas été accordé.' },
  expire: { ok: false, text: 'La demande de connexion a expiré ou n’a pas abouti : relancez « Connecter à Lookback ».' },
  echec: { ok: false, text: 'Lookback n’a pas validé la connexion. Réessayez ; si cela persiste, vérifiez que MCP est activé dans les réglages d’équipe Lookback.' },
};

/** Message après le retour de Lookback (`?lookback=…`), puis adresse nettoyée. */
export function LookbackReturnNotice() {
  const [status] = useState(() => new URLSearchParams(window.location.search).get('lookback'));
  const [visible, setVisible] = useState(Boolean(status && RETURN_MESSAGES[status]));
  useEffect(() => {
    if (!status) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('lookback');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [status]);
  const msg = status ? RETURN_MESSAGES[status] : undefined;
  if (!visible || !msg) return null;
  return (
    <Note tone={msg.ok ? 'succes' : 'avertissement'} role="status">
      {msg.text}{' '}
      <Button variant="link" size="s" type="button" onClick={() => setVisible(false)}>Fermer</Button>
    </Note>
  );
}

// --- Création ---------------------------------------------------------------------

/** Créer le connecteur, puis partir aussitôt sur Lookback donner son accord. */
export function LookbackCreateForm({ projectId }: { projectId: string }) {
  const save = useSaveConnector(projectId);
  const connect = useStartConnect();
  const [label, setLabel] = useState('Lookback');

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const { connector } = await save.mutateAsync({ kind: 'lookback', label: label.trim() || 'Lookback' });
      connect.start(connector.id);
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="write-form" onSubmit={submit} noValidate>
      <p className="zy-texte-muet">
        Lookback n’a pas de clé d’API : vous allez être redirigé vers Lookback pour autoriser Zygosight à lire vos
        projets, rounds et séances. Il faut que <strong>MCP soit activé</strong> dans les réglages d’équipe Lookback.
      </p>
      <Field label="Nom du connecteur" value={label} onChange={(e) => setLabel(e.target.value)} voice={false} />
      <p className="zy-texte-muet">
        Les séances importées deviennent des entretiens de l’étude choisie ; leurs transcriptions sont analysées comme
        les autres. La vidéo reste dans Lookback.
      </p>
      {save.error ? <ErrorBanner>{errorMessage(save.error)}</ErrorBanner> : null}
      {connect.error ? <ErrorBanner>{errorMessage(connect.error)}</ErrorBanner> : null}
      <div className="m0-actions">
        <Button type="submit" variant="accent" loading={save.isPending || connect.pending} disabled={!label.trim()}>
          Créer et connecter à Lookback
        </Button>
      </div>
    </form>
  );
}

// --- Ligne du connecteur -------------------------------------------------------------

export function LookbackRow({ projectId, connector: c, canManage }: {
  projectId: string; connector: Connector; canManage: boolean;
}) {
  const canWrite = useCanWrite(projectId);
  const connect = useStartConnect();
  const tools = useLookbackTools();
  const remove = useDeleteConnector(projectId);
  const [confirm, setConfirm] = useState(false);
  const [importing, setImporting] = useState(false);
  const close = useCallback(() => setImporting(false), []);
  const connected = Boolean(c.connectedAt);

  return (
    <li className="conn-row">
      <div className="conn-row-head">
        <div>
          <p className="conn-name">
            {c.label}
            <span className="conn-kind">Lookback</span>
          </p>
          <p className="conn-meta">
            {connected ? `Connecté le ${fmtDate(c.connectedAt!)}` : 'Compte Lookback non connecté'}
            {c.lastSyncAt && c.lastStatus === 'ok'
              ? ` · dernier import le ${fmtDate(c.lastSyncAt)} (${c.lastCount ?? 0} séance${(c.lastCount ?? 0) > 1 ? 's' : ''})`
              : ''}
          </p>
        </div>
        <span className={`conn-status conn-status-${connected ? 'ok' : c.lastStatus === 'error' ? 'error' : 'none'}`}>
          {connected ? 'Connecté' : 'À connecter'}
        </span>
      </div>

      {c.lastStatus === 'error' && c.lastError ? <p className="conn-error">{c.lastError}</p> : null}

      <div className="conn-actions">
        {connected && canWrite ? (
          <Button variant="primary" onClick={() => setImporting(true)}>Importer des séances</Button>
        ) : null}
        {canManage ? (
          <Button variant={connected ? 'ghost' : 'primary'} loading={connect.pending} onClick={() => connect.start(c.id)}>
            {connected ? 'Reconnecter' : 'Connecter à Lookback'}
          </Button>
        ) : null}
        {connected && canWrite ? (
          <Button variant="ghost" loading={tools.isPending} onClick={() => tools.mutate(c.id)}>Vérifier la connexion</Button>
        ) : null}
        {canManage ? (
          confirm ? (
            <>
              <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(c.id)}>Confirmer la suppression</Button>
              <Button variant="link" size="s" type="button" onClick={() => setConfirm(false)}>Annuler</Button>
            </>
          ) : (
            <Button variant="link-danger" size="s" type="button" onClick={() => setConfirm(true)}>Supprimer</Button>
          )
        ) : null}
      </div>
      {confirm ? <p className="zy-texte-muet">Les entretiens déjà importés restent dans leurs études.</p> : null}
      {!connected && !canManage ? (
        <Note tone="lecture">Un administrateur doit connecter le compte Lookback.</Note>
      ) : null}

      {connect.error ? <ErrorBanner>{errorMessage(connect.error)}</ErrorBanner> : null}
      {tools.error ? <ErrorBanner>{errorMessage(tools.error)}</ErrorBanner> : null}
      {tools.data ? <ToolsReport data={tools.data} /> : null}

      <Drawer
        open={importing}
        title={`Importer des séances · ${c.label}`}
        description="Choisissez un round Lookback, les séances à importer, puis l’étude qui les reçoit."
        onClose={close}
        wide
      >
        {importing ? <LookbackImportPanel projectId={projectId} connectorId={c.id} onDone={close} /> : null}
      </Drawer>
    </li>
  );
}

const ROLE_LABELS: Record<string, string> = {
  projects: 'Lister les projets',
  rounds: 'Lister les rounds',
  recordings: 'Lister les séances',
  transcript: 'Lire une transcription',
};

function ToolsReport({ data }: { data: { tools: string[]; recognized: Record<string, string | null> } }) {
  const missing = Object.values(data.recognized).some((v) => !v);
  return (
    <Note tone={missing ? 'avertissement' : 'succes'} className="conn-result" role="status">
      <p><strong>{missing ? 'Connexion établie, mais des outils manquent.' : 'Connexion réussie.'}</strong></p>
      <ul>
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <li key={role}>{label} : {data.recognized[role] ?? <em>aucun outil reconnu</em>}</li>
        ))}
      </ul>
      <p className="zy-texte-muet">Outils exposés par Lookback : {data.tools.join(', ') || 'aucun'}.</p>
    </Note>
  );
}

// --- Import --------------------------------------------------------------------------

const STATUS_LABEL: Record<LookbackImportResult['status'], string> = {
  importee: 'Importée',
  deja: 'Déjà importée',
  'sans-transcription': 'Pas de transcription dans Lookback',
  echec: 'Échec',
};

function LookbackImportPanel({ projectId, connectorId, onDone }: {
  projectId: string; connectorId: string; onDone: () => void;
}) {
  const [lbProject, setLbProject] = useState('');
  const [round, setRound] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [studyId, setStudyId] = useState('');
  const [consent, setConsent] = useState(false);
  const [results, setResults] = useState<LookbackImportResult[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const projects = useLookbackBrowse(connectorId, 'projects');
  const rounds = useLookbackBrowse(connectorId, 'rounds', { projectId: lbProject }, Boolean(lbProject));
  const recordings = useLookbackBrowse(connectorId, 'recordings', { roundId: round }, Boolean(round));
  const studies = useStudies(projectId);
  const importer = useLookbackImport(projectId, connectorId);

  // Par défaut, toutes les séances pas encore importées sont cochées.
  useEffect(() => {
    if (recordings.data) setPicked(new Set(recordings.data.filter((r) => !r.imported).map((r) => r.id)));
  }, [recordings.data]);

  const toggle = (id: string) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const chosen = (recordings.data ?? []).filter((r) => picked.has(r.id));
  const running = progress !== null && progress.done < progress.total;

  async function run() {
    setResults([]);
    setProgress({ done: 0, total: chosen.length });
    // Une séance après l'autre : chaque transcription passe par l'extraction
    // IA ; on montre l'avancée, et une séance en échec n'arrête pas les autres.
    for (const [i, rec] of chosen.entries()) {
      try {
        const r = await importer.mutateAsync({ studyId, recordings: [rec], consent });
        setResults((prev) => [...prev, ...r.results]);
      } catch (e) {
        setResults((prev) => [...prev, { id: rec.id, label: rec.participant ?? rec.name, status: 'echec', error: errorMessage(e) }]);
        // Connexion perdue ou outils introuvables : inutile de continuer.
        if (e instanceof ApiRequestError && (e.code === 'RECONNECT' || e.code === 'LOOKBACK_TOOLS')) {
          setProgress({ done: chosen.length, total: chosen.length });
          return;
        }
      }
      setProgress({ done: i + 1, total: chosen.length });
    }
  }

  const imported = results.filter((r) => r.status === 'importee');

  return (
    <div className="write-form lb-import">
      <div className="lb-steps">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="lb-project">Projet Lookback</label>
          {projects.isPending ? <Spinner label="Chargement des projets Lookback…" /> : (
            <Select id="lb-project" value={lbProject}
              onChange={(e) => { setLbProject(e.target.value); setRound(''); }}>
              <option value="">Choisir…</option>
              {(projects.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="lb-round">Round</label>
          <Select id="lb-round" value={round} disabled={!lbProject || rounds.isPending}
            onChange={(e) => setRound(e.target.value)}>
            <option value="">{lbProject && rounds.isPending ? 'Chargement…' : 'Choisir…'}</option>
            {(rounds.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
        </div>
      </div>
      {projects.error ? <ErrorBanner>{errorMessage(projects.error)}</ErrorBanner> : null}
      {rounds.error ? <ErrorBanner>{errorMessage(rounds.error)}</ErrorBanner> : null}
      {recordings.error ? <ErrorBanner>{errorMessage(recordings.error)}</ErrorBanner> : null}

      {round ? (
        recordings.isPending ? <Spinner label="Chargement des séances…" /> : (
          <div className="lb-recordings">
            <p className="opt-subtitle">Séances · {recordings.data?.length ?? 0}</p>
            {(recordings.data ?? []).length === 0 ? (
              <p className="zy-texte-muet">Aucune séance dans ce round.</p>
            ) : (
              <ul>
                {(recordings.data ?? []).map((r: LookbackItem) => (
                  <li key={r.id}>
                    <label className="lb-rec">
                      <input className="zy-check" type="checkbox" checked={picked.has(r.id)} disabled={r.imported || running}
                        onChange={() => toggle(r.id)} />
                      <span className="lb-rec-name">{r.participant ?? r.name}</span>
                      <span className="lb-rec-meta">
                        {[r.participant && r.name !== r.participant ? r.name : null, fmtDay(r.date), r.imported ? 'déjà importée' : null]
                          .filter(Boolean).join(' · ')}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      ) : null}

      <div className="zy-field">
        <label className="zy-field__label" htmlFor="lb-study">Étude qui reçoit les entretiens</label>
        <Select id="lb-study" value={studyId} onChange={(e) => setStudyId(e.target.value)} disabled={running}>
          <option value="">Choisir…</option>
          {(studies.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>{[s.code, s.objective].filter(Boolean).join(' — ') || s.id}</option>
          ))}
        </Select>
      </div>

      <label className="lb-consent">
        <input className="zy-check" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={running} />
        <span>Les participants de ces séances ont accepté d’être enregistrés et que leurs propos soient analysés.</span>
      </label>

      {progress ? (
        <p className="zy-texte-muet" role="status">
          {running ? `Import en cours… ${progress.done} / ${progress.total}` : `Terminé : ${imported.length} séance${imported.length > 1 ? 's' : ''} importée${imported.length > 1 ? 's' : ''}.`}
        </p>
      ) : null}
      {results.length > 0 ? (
        <ul className="lb-results">
          {results.map((r) => (
            <li key={r.id} className={`lb-result lb-result-${r.status}`}>
              <strong>{r.label}</strong> — {STATUS_LABEL[r.status]}
              {r.status === 'importee' ? ` · ${r.verbatims ?? 0} verbatim${(r.verbatims ?? 0) > 1 ? 's' : ''}` : ''}
              {r.error ? ` · ${r.error}` : ''}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="m0-actions">
        {progress && !running ? (
          <Button type="button" variant="ghost" onClick={onDone}>Fermer</Button>
        ) : null}
        <Button
          type="button"
          variant="accent"
          loading={running}
          disabled={chosen.length === 0 || !studyId || !consent || running}
          onClick={() => void run()}
        >
          {chosen.length > 1 ? `Importer ${chosen.length} séances` : 'Importer la séance'}
        </Button>
      </div>
    </div>
  );
}
